
<template>
  <div>
    <!-- Header buttons -->
    <div class="btn-group-hdr">
      <div class="btn-group btn-group-toggle">
        <button class="btn btn-secondary btn-sm" @click="cancel">Cancel</button>
        <button class="btn btn-primary btn-sm" @click="confirmSave">Save</button>
      </div>
    </div>

    <!-- Title -->
    <div class="title-hdr bg-dark p-2 text-white">
      <p class="mb-0">
        Assign User Permissions:
        <span class="badge bg-success">{{ user.display_name }}</span>
      </p>
    </div>

    <!-- Projects tabs -->
    <ul class="nav nav-tabs" id="projectTab" role="tablist">
      <li v-for="(proj, pIndex) in visibleProjects" :key="proj.project" role="presentation">
        <button
            class="nav-link"
            :class="{ active: activeProjectTab === pIndex }"
            :id="`project-tab-${pIndex}`"
            data-toggle="tab"
            :data-target="`#project-tabpane-${pIndex}`"
            type="button"
            role="tab"
            :aria-controls="`project-tabpane-${pIndex}`"
            :aria-selected="activeProjectTab === pIndex ? 'true' : 'false'"
            @click="onProjectTabClick(pIndex)"
        >
          {{ proj.project }}
        </button>
      </li>
    </ul>

    <!-- No projects fallback -->
    <div v-if="visibleProjects.length === 0" class="alert alert-info mt-2">
      No projects available for your permissions.
    </div>

    <!-- Projects tab panes -->
    <div v-else class="tab-content mt-1" id="projectTabContent">
      <div
          v-for="(proj, pIndex) in visibleProjects"
          :key="proj.project"
          class="tab-pane fade"
          :class="{ show: activeProjectTab === pIndex, active: activeProjectTab === pIndex }"
          :id="`project-tabpane-${pIndex}`"
          role="tabpanel"
          :aria-labelledby="`project-tab-${pIndex}`"
      >
        <!-- Brands tabs -->
        <ul class="nav nav-tabs" id="brandTab" role="tablist">
          <li v-for="(countries, brand, bIndex) in proj.brands" :key="brand" role="presentation">
            <button
                class="nav-link"
                :class="{ active: activeBrandTabs[pIndex] === bIndex }"
                :id="`brand-tab-${pIndex}-${bIndex}`"
                data-toggle="tab"
                :data-target="`#brand-tabpane-${pIndex}-${bIndex}`"
                type="button"
                role="tab"
                :aria-controls="`brand-tabpane-${pIndex}-${bIndex}`"
                :aria-selected="activeBrandTabs[pIndex] === bIndex ? 'true' : 'false'"
                @click="onBrandTabClick(pIndex, bIndex)"
            >
              {{ brand }}
            </button>
          </li>
        </ul>

        <!-- Brands tab panes -->
        <div class="tab-content mt-1" id="brandTabContent">
          <div
              v-for="(countries, brand, bIndex) in proj.brands"
              :key="brand"
              class="tab-pane fade"
              :class="{ show: activeBrandTabs[pIndex] === bIndex, active: activeBrandTabs[pIndex] === bIndex }"
              :id="`brand-tabpane-${pIndex}-${bIndex}`"
              role="tabpanel"
              :aria-labelledby="`brand-tab-${pIndex}-${bIndex}`"
          >
            <!-- Countries tabs -->
            <ul class="nav nav-tabs" id="countryTab" role="tablist">
              <li v-for="(perms, country, cIndex) in countries" :key="country" role="presentation">
                <button
                    class="nav-link"
                    :class="{ active: activeCountryTabs[pIndex + '-' + bIndex] === cIndex }"
                    :id="`country-tab-${pIndex}-${bIndex}-${cIndex}`"
                    data-toggle="tab"
                    :data-target="`#country-tabpane-${pIndex}-${bIndex}-${cIndex}`"
                    type="button"
                    role="tab"
                    :aria-controls="`country-tabpane-${pIndex}-${bIndex}-${cIndex}`"
                    :aria-selected="activeCountryTabs[pIndex + '-' + bIndex] === cIndex ? 'true' : 'false'"
                    @click="setActiveCountryTab(pIndex, bIndex, cIndex)"
                >
                  {{ country }}
                </button>
              </li>
            </ul>

            <!-- Countries tab panes -->
            <div class="tab-content mt-1" id="countryTabContent">
              <div
                  v-for="(perms, country, cIndex) in countries"
                  :key="country"
                  class="tab-pane fade"
                  :class="{ show: activeCountryTabs[pIndex + '-' + bIndex] === cIndex, active: activeCountryTabs[pIndex + '-' + bIndex] === cIndex }"
                  :id="`country-tabpane-${pIndex}-${bIndex}-${cIndex}`"
                  role="tabpanel"
                  :aria-labelledby="`country-tab-${pIndex}-${bIndex}-${cIndex}`"
              >
                <!-- MINMAX matrix -->
                <div v-if="proj.project === 'minmax'">
                  <div class="ck-tbl-dynamix">
                    <table class="table table-bordered table-hover table-inline-edit">
                      <thead class="thead-dark">
                      <tr>
                        <th class="h5" style="min-width:200px;">Permission (minmax)</th>
                        <!-- View select-all -->
                        <th class="h5 text-center" style="width:120px;">
                          <div class="checkbox-wrapper-26">
                            <input
                                type="checkbox"
                                :id="`minmax-head-view-${pIndex}-${bIndex}-${cIndex}`"
                                :checked="areAllMinmaxSelected(buildMinmaxRows(perms), 'view')"
                                @change="toggleAllMinmax(buildMinmaxRows(perms), 'view', $event.target.checked)"
                            />
                            <label :for="`minmax-head-view-${pIndex}-${bIndex}-${cIndex}`">
                              <div class="tick_mark"></div>
                            </label>
                          </div>
                          <div class="small mt-1">View</div>
                        </th>
                        <!-- Modify select-all -->
                        <th class="h5 text-center" style="width:120px;">
                          <div class="checkbox-wrapper-26">
                            <input
                                type="checkbox"
                                :id="`minmax-head-modify-${pIndex}-${bIndex}-${cIndex}`"
                                :checked="areAllMinmaxSelected(buildMinmaxRows(perms), 'modify')"
                                @change="toggleAllMinmax(buildMinmaxRows(perms), 'modify', $event.target.checked)"
                            />
                            <label :for="`minmax-head-modify-${pIndex}-${bIndex}-${cIndex}`">
                              <div class="tick_mark"></div>
                            </label>
                          </div>
                          <div class="small mt-1">Modify</div>
                        </th>
                        <!-- Delete select-all -->
                        <th class="h5 text-center" style="width:120px;">
                          <div class="checkbox-wrapper-26">
                            <input
                                type="checkbox"
                                :id="`minmax-head-delete-${pIndex}-${bIndex}-${cIndex}`"
                                :checked="areAllMinmaxSelected(buildMinmaxRows(perms), 'delete')"
                                @change="toggleAllMinmax(buildMinmaxRows(perms), 'delete', $event.target.checked)"
                            />
                            <label :for="`minmax-head-delete-${pIndex}-${bIndex}-${cIndex}`">
                              <div class="tick_mark"></div>
                            </label>
                          </div>
                          <div class="small mt-1">Delete</div>
                        </th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="row in buildMinmaxRows(perms)" :key="row.base">
                        <td class="align-middle">
                          <label class="mb-0 font-weight-bold h6">{{ row.base }}</label>
                        </td>
                        <!-- View cell -->
                        <td class="text-center align-middle">
                          <div v-if="row.viewId" class="checkbox-wrapper-26">
                            <input
                                type="checkbox"
                                :id="`minmax-view-${row.base}-${pIndex}-${bIndex}-${cIndex}`"
                                :checked="isChecked(row.viewId)"
                                :disabled="isPermissionFromRole(row.viewId)"
                                @change="togglePermission(row.viewId, $event.target.checked)"
                            />
                            <label :for="`minmax-view-${row.base}-${pIndex}-${bIndex}-${cIndex}`">
                              <div class="tick_mark"></div>
                            </label>
                          </div>
                        </td>
                        <!-- Modify cell -->
                        <td class="text-center align-middle">
                          <div v-if="row.modifyId" class="checkbox-wrapper-26">
                            <input
                                type="checkbox"
                                :id="`minmax-modify-${row.base}-${pIndex}-${bIndex}-${cIndex}`"
                                :checked="isChecked(row.modifyId)"
                                :disabled="isPermissionFromRole(row.modifyId)"
                                @change="togglePermission(row.modifyId, $event.target.checked)"
                            />
                            <label :for="`minmax-modify-${row.base}-${pIndex}-${bIndex}-${cIndex}`">
                              <div class="tick_mark"></div>
                            </label>
                          </div>
                        </td>
                        <!-- Delete cell -->
                        <td class="text-center align-middle">
                          <div v-if="row.deleteId" class="checkbox-wrapper-26">
                            <input
                                type="checkbox"
                                :id="`minmax-delete-${row.base}-${pIndex}-${bIndex}-${cIndex}`"
                                :checked="isChecked(row.deleteId)"
                                :disabled="isPermissionFromRole(row.deleteId)"
                                @change="togglePermission(row.deleteId, $event.target.checked)"
                            />
                            <label :for="`minmax-delete-${row.base}-${pIndex}-${bIndex}-${cIndex}`">
                              <div class="tick_mark"></div>
                            </label>
                          </div>
                        </td>
                      </tr>
                      <tr v-if="buildMinmaxRows(perms).length === 0">
                        <td colspan="4" class="text-center text-muted py-3">No minmax permissions</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Default (non-minmax) -->
                <div v-else class="ck-tbl-dynamix">
                  <table class="table table-bordered table-hover table-inline-edit">
                    <thead class="thead-dark">
                    <tr>
                      <th style="width:60px; text-align:center;">
                        <div class="checkbox-wrapper-26">
                          <input
                              type="checkbox"
                              :id="`select-all-perms-${pIndex}-${bIndex}-${cIndex}`"
                              :checked="areAllPermissionsSelected(sortedPermissions(perms))"
                              @change="toggleAllPermissions(sortedPermissions(perms), $event.target.checked)"
                          />
                          <label :for="`select-all-perms-${pIndex}-${bIndex}-${cIndex}`">
                            <div class="tick_mark"></div>
                          </label>
                        </div>
                      </th>
                      <th class="h5">Permission Name</th>
                      <th class="h5">Description</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr
                        v-for="perm in sortedPermissions(perms)"
                        :key="perm.permission_id"
                        @click="toggleCheckbox(perm.permission_id)"
                        style="cursor:pointer;"
                    >
                      <td class="text-center" @click.stop>
                        <div class="checkbox-wrapper-26">
                          <input
                              type="checkbox"
                              :id="`perm-checkbox-${perm.permission_id}`"
                              :checked="isChecked(perm.permission_id)"
                              :disabled="isPermissionFromRole(perm.permission_id)"
                              @change="toggleCheckbox(perm.permission_id)"
                          />
                          <label :for="`perm-checkbox-${perm.permission_id}`">
                            <div class="tick_mark"></div>
                          </label>
                        </div>
                      </td>
                      <td><label class="mb-0 font-weight-bold h6">{{ perm.permission_name }}</label></td>
                      <td class="h6">{{ perm.description }}</td>
                    </tr>
                    <tr v-if="sortedPermissions(perms).length === 0">
                      <td colspan="3" class="text-center text-muted py-3">No permissions</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <!-- /Default -->

              </div> <!-- /country pane -->
            </div> <!-- /country tabcontent -->

          </div> <!-- /brand pane -->
        </div> <!-- /brand tabcontent -->

      </div> <!-- /project pane -->
    </div> <!-- /project tabcontent -->
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AssignUserPermissions',
  props: {
    hasPermission: { type: Function, required: true },
    user: { type: Object, required: true },
    permissions: { type: Array, default: function(){ return []; } },
    rolePermissions: { type: Array, default: function(){ return []; } }
  },
  data: function() {
    return {
      allPermissions: [],
      selectedPermissionIds: [],
      activeProjectTab: 0,
      activeBrandTabs: {},   // project index -> active brand index
      activeCountryTabs: {}  // "pIndex-bIndex" -> active country index
    }
  },
  computed: {
    groupedByProject: function() {
      var grouped = {};
      for (var i=0; i<this.allPermissions.length; i++) {
        var perm = this.allPermissions[i];
        var project = perm.project_name;
        var brand = perm.brand;
        var country = perm.country;
        if (!grouped[project]) grouped[project] = {};
        if (!grouped[project][brand]) grouped[project][brand] = {};
        if (!grouped[project][brand][country]) grouped[project][brand][country] = [];
        grouped[project][brand][country].push(perm);
      }
      function sortKeysWithPriority(keys, priority) {
        var set = {}; for (var k=0;k<priority.length;k++) set[priority[k]] = true;
        var pri=[], rest=[];
        for (var i2=0;i2<keys.length;i2++) { var key=keys[i2]; if (set[key]) pri.push(key); else rest.push(key); }
        rest.sort(); return pri.concat(rest);
      }
      var sortedProjects = sortKeysWithPriority(Object.keys(grouped), ['User Management']);
      var out = {};
      for (var p=0;p<sortedProjects.length;p++) {
        var project = sortedProjects[p];
        var brands = grouped[project];
        var brandKeys = sortKeysWithPriority(Object.keys(brands), ['Charles & Keith']);
        out[project] = {};
        for (var b=0;b<brandKeys.length;b++) {
          var brand = brandKeys[b];
          var countries = brands[brand];
          var countryKeys = sortKeysWithPriority(Object.keys(countries), ['Singapore']);
          out[project][brand] = {};
          for (var c=0;c<countryKeys.length;c++) {
            var country = countryKeys[c];
            out[project][brand][country] = countries[country];
          }
        }
      }
      return out;
    },
    visibleProjects: function() {
      var out = []; var source = this.groupedByProject;
      for (var project in source) {
        if (this.canSeeProject(project)) out.push({ project: project, brands: source[project] });
      }
      return out;
    }
  },
  mounted: function() {
    this.fetchAllPermissions();
  },
  methods: {
    canSeeProject: function(project) {
      if (project === 'User Management') return this.hasPermission('Assign User Management Permissions');
      if (project === 'minmax')          return this.hasPermission('Assign DRP Permissions');
      if (project === 'store')           return this.hasPermission('Assign Store Permissions');
      return false;
    },
    fetchAllPermissions: function() {
      var vm = this;
      axios.get('/user/permissions')
          .then(function(res){
            if (res.data && res.data.permissions) {
              vm.allPermissions = res.data.permissions;
              vm.selectedPermissionIds = vm.permissions.map(function(p){ return p.permission_id; });

              vm.activeProjectTab = 0;
              var projects = vm.visibleProjects;
              for (var pIndex=0;pIndex<projects.length;pIndex++) {
                vm.$set(vm.activeBrandTabs, pIndex, 0);
                var brands = Object.keys(projects[pIndex].brands);
                for (var bIndex=0;bIndex<brands.length;bIndex++) {
                  var key = pIndex + '-' + bIndex;
                  vm.$set(vm.activeCountryTabs, key, 0);
                }
              }
            } else {
              vm.allPermissions = [];
            }
          })
          .catch(function(err){
            console.error('Failed to fetch permissions:', err);
            vm.allPermissions = [];
          });
    },

    // role inheritance
    isPermissionFromRole: function(permissionId) {
      for (var i=0;i<this.rolePermissions.length;i++) {
        if (this.rolePermissions[i].permission_id === permissionId) return true;
      }
      return false;
    },
    isChecked: function(permissionId) {
      if (this.isPermissionFromRole(permissionId)) return true;
      return this.selectedPermissionIds.indexOf(permissionId) !== -1;
    },

    // default table helpers
    sortedPermissions: function(perms) {
      var copy = perms.slice();
      copy.sort(function(a,b){
        var A=(a.permission_name||'').toLowerCase(), B=(b.permission_name||'').toLowerCase();
        if (A<B) return -1; if (A>B) return 1; return 0;
      });
      return copy;
    },
    areAllPermissionsSelected: function(perms) {
      var selectable=[];
      for (var i=0;i<perms.length;i++) {
        var id = perms[i].permission_id;
        if (!this.isPermissionFromRole(id)) selectable.push(id);
      }
      if (selectable.length===0) return true;
      for (var j=0;j<selectable.length;j++) {
        if (this.selectedPermissionIds.indexOf(selectable[j])===-1) return false;
      }
      return true;
    },
    toggleAllPermissions: function(perms, checked) {
      var ids=[];
      for (var i=0;i<perms.length;i++) {
        var id=perms[i].permission_id;
        if (!this.isPermissionFromRole(id)) ids.push(id);
      }
      if (checked) {
        var set={}; for (var s=0;s<this.selectedPermissionIds.length;s++) set[this.selectedPermissionIds[s]]=true;
        for (var k=0;k<ids.length;k++) set[ids[k]]=true;
        this.selectedPermissionIds = Object.keys(set);
      } else {
        var remove={}; for (var r=0;r<ids.length;r++) remove[ids[r]]=true;
        this.selectedPermissionIds = this.selectedPermissionIds.filter(function(id){ return !remove[id]; });
      }
    },
    toggleCheckbox: function(permissionId) {
      if (this.isPermissionFromRole(permissionId)) return;
      var idx=this.selectedPermissionIds.indexOf(permissionId);
      if (idx===-1) this.selectedPermissionIds.push(permissionId);
      else this.selectedPermissionIds.splice(idx,1);
    },

    // minmax helpers
    parseMinmaxName: function(name) {
      var nm=name||'';
      if (nm.length>=7 && nm.substr(0,7).toLowerCase()==='modify ') return { base: nm.substr(7), type:'modify' };
      if (nm.length>=7 && nm.substr(0,7).toLowerCase()==='delete ') return { base: nm.substr(7), type:'delete' };
      return { base: nm, type:'view' };
    },
    buildMinmaxRows: function(perms) {
      var map={};
      for (var i=0;i<perms.length;i++) {
        var p=perms[i];
        if (!p || p.project_name!=='minmax') continue;
        var parsed=this.parseMinmaxName(p.permission_name);
        var base=parsed.base, type=parsed.type;
        if (!map[base]) map[base]={ base:base, viewId:null, modifyId:null, deleteId:null };
        if (type==='view')   map[base].viewId   = p.permission_id;
        if (type==='modify') map[base].modifyId = p.permission_id;
        if (type==='delete') map[base].deleteId = p.permission_id;
      }
      var rows=[]; for (var key in map) rows.push(map[key]);
      rows.sort(function(a,b){ var A=(a.base||'').toLowerCase(), B=(b.base||'').toLowerCase(); if (A<B) return -1; if (A>B) return 1; return 0; });
      return rows;
    },
    areAllMinmaxSelected: function(rows, type) {
      var hasAny=false;
      for (var i=0;i<rows.length;i++) {
        var id=(type==='view')?rows[i].viewId:(type==='modify')?rows[i].modifyId:rows[i].deleteId;
        if (id && !this.isPermissionFromRole(id)) {
          hasAny=true;
          if (this.selectedPermissionIds.indexOf(id)===-1) return false;
        }
      }
      return hasAny;
    },
    toggleAllMinmax: function(rows, type, checked) {
      if (checked) {
        var set={}; for (var i=0;i<this.selectedPermissionIds.length;i++) set[this.selectedPermissionIds[i]]=true;
        for (var r=0;r<rows.length;r++) { var id=(type==='view')?rows[r].viewId:(type==='modify')?rows[r].modifyId:rows[r].deleteId; if (id && !this.isPermissionFromRole(id)) set[id]=true; }
        this.selectedPermissionIds = Object.keys(set);
      } else {
        var toRemove={}; for (var r2=0;r2<rows.length;r2++) { var id2=(type==='view')?rows[r2].viewId:(type==='modify')?rows[r2].modifyId:rows[r2].deleteId; if (id2 && !this.isPermissionFromRole(id2)) toRemove[id2]=true; }
        this.selectedPermissionIds = this.selectedPermissionIds.filter(function(id){ return !toRemove[id]; });
      }
    },
    togglePermission: function(permissionId, checked) {
      if (!permissionId || this.isPermissionFromRole(permissionId)) return;
      var idx=this.selectedPermissionIds.indexOf(permissionId);
      if (checked && idx===-1) this.selectedPermissionIds.push(permissionId);
      if (!checked && idx!==-1) this.selectedPermissionIds.splice(idx,1);
    },

    // save/cancel
    cancel: function() {
      this.$emit('cancel');
    },
    confirmSave: function() {
      var vm=this;
      Swal.fire({
        title: 'Are you sure?',
        text: 'Do you want to save the permission changes?',
        type: 'warning', // SweetAlert2 v7/8
        showCancelButton: true,
        confirmButtonText: 'Yes, save it!',
        cancelButtonText: 'Cancel'
      }).then(function(result){
        if (result && result.value) vm.savePermissions();
      });
    },
    savePermissions: function() {
      this.$emit('save', { userId: this.user.user_id, permissions: this.selectedPermissionIds });
    },

    // tabs
    onProjectTabClick: function(pIndex) {
      this.activeProjectTab = pIndex;
      if (typeof this.activeBrandTabs[pIndex] === 'undefined') this.$set(this.activeBrandTabs, pIndex, 0);
      var proj=this.visibleProjects[pIndex];
      if (proj) {
        var brands=Object.keys(proj.brands);
        if (brands.length>0) {
          var key=pIndex+'-0';
          if (typeof this.activeCountryTabs[key] === 'undefined') this.$set(this.activeCountryTabs, key, 0);
        }
      }
    },
    onBrandTabClick: function(pIndex,bIndex) {
      this.$set(this.activeBrandTabs, pIndex, bIndex);
      var key=pIndex+'-'+bIndex;
      if (typeof this.activeCountryTabs[key] === 'undefined') this.$set(this.activeCountryTabs, key, 0);
    },
    setActiveCountryTab: function(pIndex,bIndex,cIndex) {
      var key=pIndex+'-'+bIndex;
      this.$set(this.activeCountryTabs, key, cIndex);
    }
  },
  watch: {
    permissions: {
      immediate: true,
      handler: function(newPermissions) {
        this.selectedPermissionIds = newPermissions.map(function(p){ return p.permission_id; });
      }
    }
  }
}
</script>

<style scoped>
/* Round animated checkbox (scaled for tables) */
.checkbox-wrapper-26 * { -webkit-tap-highlight-color: transparent; outline: none; }
.checkbox-wrapper-26 input[type="checkbox"] { display: none; }
.checkbox-wrapper-26 label {
  --size: 22px;
  --shadow: calc(var(--size) * .07) calc(var(--size) * .1);
  position: relative; display: inline-block;
  width: var(--size); height: var(--size); margin: 0;
  background-color: #f72414; border-radius: 50%;
  box-shadow: 0 var(--shadow) #ffbeb8;
  cursor: pointer; overflow: hidden; z-index: 1;
  transition: 0.2s ease transform, 0.2s ease background-color, 0.2s ease box-shadow;
  vertical-align: middle;
}
.checkbox-wrapper-26 label:before {
  content: ""; position: absolute; top: 50%; right: 0; left: 0;
  width: calc(var(--size) * .7); height: calc(var(--size) * .7);
  margin: 0 auto; background-color: #fff; transform: translateY(-50%);
  border-radius: 50%; box-shadow: inset 0 var(--shadow) #ffbeb8;
  transition: 0.2s ease width, 0.2s ease height;
}
.checkbox-wrapper-26 label:hover:before { width: calc(var(--size) * .60); height: calc(var(--size) * .60); box-shadow: inset 0 var(--shadow) #ff9d96; }
.checkbox-wrapper-26 label:active { transform: scale(0.95); }
.checkbox-wrapper-26 .tick_mark {
  position: absolute; top: -1px; right: 0; left: calc(var(--size) * -.05);
  width: calc(var(--size) * .6); height: calc(var(--size) * .6);
  margin: 0 auto; margin-left: calc(var(--size) * .14); transform: rotateZ(-40deg);
}
.checkbox-wrapper-26 .tick_mark:before,
.checkbox-wrapper-26 .tick_mark:after {
  content: ""; position: absolute; background-color: #fff; border-radius: 2px;
  opacity: 0; transition: 0.2s ease transform, 0.2s ease opacity;
}
.checkbox-wrapper-26 .tick_mark:before {
  left: 0; bottom: 0; width: calc(var(--size) * .1); height: calc(var(--size) * .3);
  box-shadow: -1px 0 3px rgba(0,0,0,0.23); transform: translateY(calc(var(--size) * -.68));
}
.checkbox-wrapper-26 .tick_mark:after {
  left: 0; bottom: 0; width: 100%; height: calc(var(--size) * .1);
  box-shadow: 0 2px 3px rgba(0,0,0,0.23); transform: translateX(calc(var(--size) * .78));
}
.checkbox-wrapper-26 input[type="checkbox"]:checked + label { background-color: #07d410; box-shadow: 0 var(--shadow) #92ff97; }
.checkbox-wrapper-26 input[type="checkbox"]:checked + label:before { width: 0; height: 0; }
.checkbox-wrapper-26 input[type="checkbox"]:checked + label .tick_mark:before,
.checkbox-wrapper-26 input[type="checkbox"]:checked + label .tick_mark:after { transform: translate(0); opacity: 1; }

/* Table alignment */
.table th, .table td { vertical-align: middle; }



/* DISABLED STATE */
.checkbox-wrapper-26 input[type="checkbox"][disabled] + label,
.checkbox-wrapper-26 input[type="checkbox"]:disabled + label {
  cursor: not-allowed;
  background-color: #d6d6d6;          /* outer circle */
  box-shadow: 0 var(--shadow) #e8e8e8;
  /* lock animations for disabled */
  transition: none;
}

.checkbox-wrapper-26 input[type="checkbox"][disabled] + label:before,
.checkbox-wrapper-26 input[type="checkbox"]:disabled + label:before {
  background-color: #f5f5f5;          /* inner disc */
  box-shadow: inset 0 var(--shadow) #eaeaea;
  transition: none;
}

/* prevent hover/active effects when disabled */
.checkbox-wrapper-26 input[type="checkbox"][disabled] + label:hover:before,
.checkbox-wrapper-26 input[type="checkbox"]:disabled + label:hover:before {
  width: calc(var(--size) * .70);
  height: calc(var(--size) * .70);
  box-shadow: inset 0 var(--shadow) #eaeaea;
}
.checkbox-wrapper-26 input[type="checkbox"][disabled] + label:active,
.checkbox-wrapper-26 input[type="checkbox"]:disabled + label:active {
  transform: none;
}

/* disabled + checked: keep tick but mute colors */
.checkbox-wrapper-26 input[type="checkbox"][disabled]:checked + label,
.checkbox-wrapper-26 input[type="checkbox"]:disabled:checked + label {
  background-color: #e0f8dd;          /* muted green -> grey */
  box-shadow: 0 var(--shadow) #e2e2e2;
}

.checkbox-wrapper-26 input[type="checkbox"][disabled]:checked + label:before,
.checkbox-wrapper-26 input[type="checkbox"]:disabled:checked + label:before {
  /* keep hidden disc as in checked, no transition */
  width: 0; height: 0; transition: none;
}

.checkbox-wrapper-26 input[type="checkbox"][disabled]:checked + label .tick_mark:before,
.checkbox-wrapper-26 input[type="checkbox"]:disabled:checked + label .tick_mark:before,
.checkbox-wrapper-26 input[type="checkbox"][disabled]:checked + label .tick_mark:after,
.checkbox-wrapper-26 input[type="checkbox"]:disabled:checked + label .tick_mark:after {
  background-color: #325e04;
  box-shadow: 0 var(--shadow) #effaf0;
  opacity: 1;
  transition: none;
}

/* Optional: visually reduce emphasis on the whole control when disabled */
.checkbox-wrapper-26.is-disabled label {
  filter: grayscale(15%);
  opacity: 0.7;
}
</style>



// WEBPACK FOOTER //
// src/components/users/users/AssignUserPermissions.vue